import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabaseClient';
import ImageUploader from '@/components/base/ImageUploader';

interface HeroSlide {
  id: string;
  sort_order: number;
  name: string;
  tag: string;
  description: string;
  detail: string;
  price: string;
  image_url: string;
  accent_color: string;
  accent_light: string;
  is_active: boolean;
}

const ACCENT_PRESETS = [
  { label: 'Amber', color: '#F59E0B', light: '#FDE68A' },
  { label: 'Kırmızı', color: '#EF4444', light: '#FECACA' },
  { label: 'Yeşil', color: '#10B981', light: '#A7F3D0' },
  { label: 'Turuncu', color: '#F97316', light: '#FED7AA' },
  { label: 'Mor', color: '#8B5CF6', light: '#DDD6FE' },
  { label: 'Pembe', color: '#EC4899', light: '#FBCFE8' },
];

const emptySlide = (): Omit<HeroSlide, 'id'> => ({
  sort_order: 99,
  name: '',
  tag: '',
  description: '',
  detail: '',
  price: '',
  image_url: '',
  accent_color: '#F59E0B',
  accent_light: '#FDE68A',
  is_active: true,
});

const SliderTab = () => {
  const [slides, setSlides] = useState<HeroSlide[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Omit<HeroSlide, 'id'>>(emptySlide());
  const [isAdding, setIsAdding] = useState(false);
  const [addForm, setAddForm] = useState<Omit<HeroSlide, 'id'>>(emptySlide());
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [previewSlide, setPreviewSlide] = useState<HeroSlide | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showToast = (type: 'success' | 'error', text: string) => {
    setToast({ type, text });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 3500);
  };

  const fetchSlides = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('hero_slides')
      .select('*')
      .order('sort_order', { ascending: true });
    if (!error && data) setSlides(data as HeroSlide[]);
    setLoading(false);
  };

  useEffect(() => {
    fetchSlides();
  }, []);

  const handleEdit = (slide: HeroSlide) => {
    setEditingId(slide.id);
    setEditForm({
      sort_order: slide.sort_order,
      name: slide.name,
      tag: slide.tag,
      description: slide.description,
      detail: slide.detail,
      price: slide.price,
      image_url: slide.image_url,
      accent_color: slide.accent_color,
      accent_light: slide.accent_light,
      is_active: slide.is_active,
    });
    setIsAdding(false);
  };

  const handleSaveEdit = async () => {
    if (!editingId) return;
    if (!editForm.name.trim()) { showToast('error', 'Slide adı boş olamaz.'); return; }
    setSaving(true);
    const { error } = await supabase
      .from('hero_slides')
      .update({ ...editForm, updated_at: new Date().toISOString() })
      .eq('id', editingId);
    setSaving(false);
    if (error) { showToast('error', 'Kayıt sırasında hata oluştu.'); return; }
    showToast('success', 'Slide başarıyla güncellendi.');
    setEditingId(null);
    fetchSlides();
  };

  const handleAdd = async () => {
    if (!addForm.name.trim()) { showToast('error', 'Slide adı boş olamaz.'); return; }
    setSaving(true);
    const { error } = await supabase.from('hero_slides').insert([addForm]);
    setSaving(false);
    if (error) { showToast('error', 'Eklenirken hata oluştu.'); return; }
    showToast('success', 'Yeni slide eklendi.');
    setIsAdding(false);
    setAddForm(emptySlide());
    fetchSlides();
  };

  const handleDelete = async (id: string) => {
    setDeleting(id);
    const { error } = await supabase.from('hero_slides').delete().eq('id', id);
    setDeleting(null);
    if (error) { showToast('error', 'Silme sırasında hata oluştu.'); return; }
    showToast('success', 'Slide silindi.');
    if (editingId === id) setEditingId(null);
    fetchSlides();
  };

  const handleToggleActive = async (slide: HeroSlide) => {
    const { error } = await supabase
      .from('hero_slides')
      .update({ is_active: !slide.is_active, updated_at: new Date().toISOString() })
      .eq('id', slide.id);
    if (!error) fetchSlides();
  };

  const handleMoveUp = async (slide: HeroSlide, idx: number) => {
    if (idx === 0) return;
    const prev = slides[idx - 1];
    await supabase.from('hero_slides').update({ sort_order: prev.sort_order }).eq('id', slide.id);
    await supabase.from('hero_slides').update({ sort_order: slide.sort_order }).eq('id', prev.id);
    fetchSlides();
  };

  const handleMoveDown = async (slide: HeroSlide, idx: number) => {
    if (idx === slides.length - 1) return;
    const next = slides[idx + 1];
    await supabase.from('hero_slides').update({ sort_order: next.sort_order }).eq('id', slide.id);
    await supabase.from('hero_slides').update({ sort_order: slide.sort_order }).eq('id', next.id);
    fetchSlides();
  };

  const renderForm = (
    form: Omit<HeroSlide, 'id'>,
    setForm: (f: Omit<HeroSlide, 'id'>) => void,
    onSave: () => void,
    onCancel: () => void,
    title: string,
  ) => (
    <div className="bg-white rounded-2xl border border-orange-100 p-6 mb-4">
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-base font-bold text-gray-900">{title}</h3>
        <button onClick={onCancel} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer transition-colors">
          <i className="ri-close-line text-lg"></i>
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Name */}
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1.5">Ürün Adı *</label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Örn: Döner Teller"
            className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-900 placeholder-gray-400 outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-50 transition-all"
          />
        </div>

        {/* Tag */}
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1.5">Etiket / Rozet</label>
          <input
            type="text"
            value={form.tag}
            onChange={(e) => setForm({ ...form, tag: e.target.value })}
            placeholder="Örn: Bestseller, Neu, Beliebt"
            className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-900 placeholder-gray-400 outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-50 transition-all"
          />
        </div>

        {/* Price */}
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1.5">Fiyat</label>
          <input
            type="text"
            value={form.price}
            onChange={(e) => setForm({ ...form, price: e.target.value })}
            placeholder="Örn: ab CHF 16.50"
            className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-900 placeholder-gray-400 outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-50 transition-all"
          />
        </div>

        {/* Sort order */}
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1.5">Sıra Numarası</label>
          <input
            type="number"
            value={form.sort_order}
            onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })}
            className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-900 outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-50 transition-all"
          />
        </div>

        {/* Description */}
        <div className="md:col-span-2">
          <label className="block text-xs font-semibold text-gray-600 mb-1.5">Açıklama</label>
          <textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Ürün açıklaması..."
            rows={2}
            maxLength={500}
            className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-900 placeholder-gray-400 outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-50 transition-all resize-none"
          />
        </div>

        {/* Detail */}
        <div className="md:col-span-2">
          <label className="block text-xs font-semibold text-gray-600 mb-1.5">Alt Detay Metni</label>
          <input
            type="text"
            value={form.detail}
            onChange={(e) => setForm({ ...form, detail: e.target.value })}
            placeholder="Örn: Frisch zubereitet · Täglich geöffnet"
            className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-900 placeholder-gray-400 outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-50 transition-all"
          />
        </div>

        {/* Image URL + Upload */}
        <div className="md:col-span-2">
          <ImageUploader
            value={form.image_url}
            onChange={(url) => setForm({ ...form, image_url: url })}
            label="Ürün Görseli"
            placeholder="https://... veya aşağıdan fotoğraf yükle"
            previewShape="circle"
            folder="slider"
          />
        </div>

        {/* Accent color */}
        <div className="md:col-span-2">
          <label className="block text-xs font-semibold text-gray-600 mb-2">Vurgu Rengi</label>
          <div className="flex flex-wrap gap-2 mb-3">
            {ACCENT_PRESETS.map((p) => (
              <button
                key={p.color}
                onClick={() => setForm({ ...form, accent_color: p.color, accent_light: p.light })}
                className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold border-2 transition-all cursor-pointer whitespace-nowrap"
                style={{
                  borderColor: form.accent_color === p.color ? p.color : 'transparent',
                  background: `${p.color}18`,
                  color: p.color,
                }}
              >
                <span
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ background: p.color }}
                />
                {p.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-500">Özel renk:</label>
              <input
                type="color"
                value={form.accent_color}
                onChange={(e) => setForm({ ...form, accent_color: e.target.value })}
                className="w-8 h-8 rounded-lg border border-gray-200 cursor-pointer"
              />
              <span className="text-xs text-gray-400 font-mono">{form.accent_color}</span>
            </div>
            <div
              className="flex-1 h-8 rounded-xl border border-gray-100 flex items-center justify-center text-xs font-bold"
              style={{ background: form.accent_color, color: '#fff' }}
            >
              Önizleme
            </div>
          </div>
        </div>

        {/* Active toggle */}
        <div className="md:col-span-2 flex items-center gap-3">
          <button
            onClick={() => setForm({ ...form, is_active: !form.is_active })}
            className={`relative w-11 h-6 rounded-full transition-all cursor-pointer flex-shrink-0 ${form.is_active ? 'bg-green-500' : 'bg-gray-300'}`}
          >
            <span
              className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${form.is_active ? 'left-5' : 'left-0.5'}`}
            />
          </button>
          <span className="text-sm text-gray-700 font-medium">
            {form.is_active ? 'Aktif — Ana sayfada görünür' : 'Pasif — Gizli'}
          </span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3 mt-6 pt-5 border-t border-gray-100">
        <button
          onClick={onSave}
          disabled={saving}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold transition-all cursor-pointer disabled:opacity-50 whitespace-nowrap"
        >
          {saving ? (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          ) : (
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-save-line"></i>
            </div>
          )}
          Kaydet
        </button>
        <button
          onClick={onCancel}
          className="px-5 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-semibold hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap"
        >
          İptal
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col gap-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-1">Ana Sayfa Slider</h2>
          <p className="text-gray-500 text-sm">Hero bölümündeki slider kartlarını yönetin</p>
        </div>
        <button
          onClick={() => { setIsAdding(true); setEditingId(null); setAddForm(emptySlide()); }}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold transition-all cursor-pointer whitespace-nowrap"
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-add-line"></i>
          </div>
          Yeni Slide Ekle
        </button>
      </div>

      {/* Toast */}
      {toast && (
        <div className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium ${
          toast.type === 'success' ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-red-50 text-red-600 border border-red-100'
        }`}>
          <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
            <i className={toast.type === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'}></i>
          </div>
          {toast.text}
        </div>
      )}

      {/* Add form */}
      {isAdding && renderForm(
        addForm,
        setAddForm,
        handleAdd,
        () => setIsAdding(false),
        'Yeni Slide Ekle',
      )}

      {/* Slides list */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : slides.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
          <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 text-gray-300">
            <i className="ri-slideshow-3-line text-4xl"></i>
          </div>
          <p className="text-gray-500 text-sm">Henüz slide yok. Yeni slide ekleyin.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {slides.map((slide, idx) => (
            <div key={slide.id}>
              {/* Edit form inline */}
              {editingId === slide.id && renderForm(
                editForm,
                setEditForm,
                handleSaveEdit,
                () => setEditingId(null),
                `Düzenle: ${slide.name}`,
              )}

              {/* Slide card */}
              {editingId !== slide.id && (
                <div
                  className={`bg-white rounded-2xl border transition-all ${
                    slide.is_active ? 'border-gray-100' : 'border-gray-100 opacity-60'
                  }`}
                >
                  <div className="flex items-center gap-4 p-4">
                    {/* Sort controls */}
                    <div className="flex flex-col gap-1 flex-shrink-0">
                      <button
                        onClick={() => handleMoveUp(slide, idx)}
                        disabled={idx === 0}
                        className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 disabled:opacity-30 cursor-pointer transition-colors"
                      >
                        <i className="ri-arrow-up-s-line text-base"></i>
                      </button>
                      <span className="text-xs text-gray-400 text-center font-mono">{idx + 1}</span>
                      <button
                        onClick={() => handleMoveDown(slide, idx)}
                        disabled={idx === slides.length - 1}
                        className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 disabled:opacity-30 cursor-pointer transition-colors"
                      >
                        <i className="ri-arrow-down-s-line text-base"></i>
                      </button>
                    </div>

                    {/* Image */}
                    <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 border border-gray-100">
                      {slide.image_url ? (
                        <img src={slide.image_url} alt={slide.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                          <i className="ri-image-line text-gray-400 text-xl"></i>
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-gray-900 text-sm truncate">{slide.name}</span>
                        {slide.tag && (
                          <span
                            className="text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0"
                            style={{ background: `${slide.accent_color}20`, color: slide.accent_color }}
                          >
                            {slide.tag}
                          </span>
                        )}
                        {!slide.is_active && (
                          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 flex-shrink-0">
                            Pasif
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 truncate mb-1">{slide.description}</p>
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-bold" style={{ color: slide.accent_color }}>{slide.price}</span>
                        <span
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={{ background: slide.accent_color }}
                          title={slide.accent_color}
                        />
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {/* Preview */}
                      <button
                        onClick={() => setPreviewSlide(slide)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 cursor-pointer transition-colors"
                        title="Önizle"
                      >
                        <i className="ri-eye-line text-base"></i>
                      </button>

                      {/* Toggle active */}
                      <button
                        onClick={() => handleToggleActive(slide)}
                        className={`w-8 h-8 flex items-center justify-center rounded-lg cursor-pointer transition-colors ${
                          slide.is_active
                            ? 'hover:bg-green-50 text-green-500'
                            : 'hover:bg-gray-100 text-gray-400'
                        }`}
                        title={slide.is_active ? 'Pasife al' : 'Aktife al'}
                      >
                        <i className={slide.is_active ? 'ri-toggle-fill text-base' : 'ri-toggle-line text-base'}></i>
                      </button>

                      {/* Edit */}
                      <button
                        onClick={() => handleEdit(slide)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-orange-50 text-gray-400 hover:text-orange-500 cursor-pointer transition-colors"
                        title="Düzenle"
                      >
                        <i className="ri-edit-line text-base"></i>
                      </button>

                      {/* Delete */}
                      <button
                        onClick={() => handleDelete(slide.id)}
                        disabled={deleting === slide.id}
                        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 cursor-pointer transition-colors disabled:opacity-50"
                        title="Sil"
                      >
                        {deleting === slide.id ? (
                          <div className="w-3.5 h-3.5 border-2 border-red-400 border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          <i className="ri-delete-bin-line text-base"></i>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Info box */}
      <div className="bg-orange-50 rounded-2xl border border-orange-100 p-5">
        <div className="flex items-start gap-3">
          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
            <i className="ri-information-line text-orange-500"></i>
          </div>
          <div>
            <p className="text-sm font-semibold text-orange-800 mb-1">Slider Hakkında</p>
            <p className="text-xs text-orange-700 leading-relaxed">
              Değişiklikler anında ana sayfaya yansır. Sıra numaralarını yukarı/aşağı oklarıyla değiştirebilirsiniz.
              Pasif slideler ana sayfada görünmez. En az 1 aktif slide olması önerilir.
            </p>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      {previewSlide && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
          onClick={() => setPreviewSlide(null)}
        >
          <div
            className="relative w-full max-w-2xl mx-4 rounded-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)' }}
          >
            {/* Close */}
            <button
              onClick={() => setPreviewSlide(null)}
              className="absolute top-4 right-4 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-white/20 text-white cursor-pointer hover:bg-white/30 transition-colors"
            >
              <i className="ri-close-line text-lg"></i>
            </button>

            <div className="flex items-center gap-6 p-8">
              {/* Left */}
              <div className="flex-1">
                <span
                  className="text-xs font-black tracking-widest uppercase mb-3 block"
                  style={{ color: previewSlide.accent_color }}
                >
                  Uzi Imb
                </span>
                <div
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold mb-3"
                  style={{ background: `${previewSlide.accent_color}25`, color: previewSlide.accent_color }}
                >
                  ★ {previewSlide.tag}
                </div>
                <h3 className="text-3xl font-black text-white mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {previewSlide.name}
                </h3>
                <p className="text-white/70 text-sm mb-2">{previewSlide.description}</p>
                <p className="text-white/40 text-xs mb-4">{previewSlide.detail}</p>
                <span className="text-2xl font-black" style={{ color: previewSlide.accent_color }}>
                  {previewSlide.price}
                </span>
              </div>

              {/* Right */}
              <div className="flex-shrink-0">
                <div
                  className="w-40 h-40 rounded-full overflow-hidden border-4"
                  style={{ borderColor: previewSlide.accent_color }}
                >
                  {previewSlide.image_url ? (
                    <img src={previewSlide.image_url} alt={previewSlide.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-gray-700 flex items-center justify-center">
                      <i className="ri-image-line text-gray-500 text-3xl"></i>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="px-8 pb-4 text-xs text-white/30 text-center">Önizleme — Gerçek görünüm farklı olabilir</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SliderTab;
